import type { Event } from '@/types/domain';

/**
 * Date-scope filter for the Events tab. Applies to both the list and the map
 * (they share one filtered array), so the scope narrows both together.
 *
 * All windows are anchored to America/New_York calendar days — the tournament's
 * timezone — so "today" means today *here*, regardless of the device's tz.
 * Pure + deterministic (takes `now`), so it's unit-testable.
 */
export type DateScope = 'all' | 'today' | 'weekend' | 'week';

// Display order; "all" is the default no-op.
export const DATE_SCOPES: DateScope[] = ['all', 'today', 'weekend', 'week'];

const TZ = 'America/New_York';
const DAY_MS = 24 * 60 * 60 * 1000;
const ET_WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// YYYY-MM-DD in ET — stable calendar-day key across DST and device tz.
function etYMD(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: TZ });
}

function etWeekday(d: Date): number {
  return ET_WEEKDAYS.indexOf(d.toLocaleDateString('en-US', { timeZone: TZ, weekday: 'short' }));
}

// Step by whole days from a noon anchor so a 23/25-hour DST day never slips the
// result into the wrong ET calendar date.
function etYMDOffset(now: Date, offsetDays: number): string {
  const anchor = new Date(now.getTime());
  anchor.setHours(12, 0, 0, 0);
  return etYMD(new Date(anchor.getTime() + offsetDays * DAY_MS));
}

// The ET calendar days that count as "this weekend". On a Sunday that's
// yesterday (Sat) + today (Sun); otherwise the coming Sat + Sun (today itself
// when it's already Saturday).
function weekendYMDs(now: Date): Set<string> {
  const wd = etWeekday(now);
  const satOffset = wd === 0 ? -1 : 6 - wd;
  return new Set([etYMDOffset(now, satOffset), etYMDOffset(now, satOffset + 1)]);
}

/**
 * Does an event's run overlap the chosen scope? A multi-day event (e.g. an
 * all-tournament fan fest) counts as "today" / "this week" for every day
 * between its start and end, not just its start day. Point-in-time events have
 * no `endsAt` and collapse to their single start day. ET calendar days
 * throughout; YYYY-MM-DD strings compare chronologically as plain strings.
 */
export function inDateScope(ev: Event, scope: DateScope, now: Date): boolean {
  if (scope === 'all') return true;
  const startYmd = etYMD(new Date(ev.startsAt));
  const rawEnd = ev.endsAt ? etYMD(new Date(ev.endsAt)) : startYmd;
  // Guard against bad data (end before start) by collapsing to the start day.
  const endYmd = rawEnd >= startYmd ? rawEnd : startYmd;
  const overlaps = (ymd: string): boolean => startYmd <= ymd && ymd <= endYmd;

  if (scope === 'today') return overlaps(etYMD(now));
  if (scope === 'weekend') return [...weekendYMDs(now)].some(overlaps);
  // 'week' — any day from today through the next 6 (7 ET days incl. today).
  for (let i = 0; i < 7; i++) {
    if (overlaps(etYMDOffset(now, i))) return true;
  }
  return false;
}
